import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { DateTime } from 'luxon'

import { faGlobe } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, LoadingModule,
} from '@domain-group/suit-theme'

import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import { sanitiseObject } from '@functions/utility'

import usePublicationEditionsDeadlines from '@hooks/usePublicationEditionDeadlines'
import type { FilterOptions } from '@hooks/usePublicationEditionDeadlines'

import PackContext from '@contexts/packContext'
import DeadlinesTable from '../DeadlinesTable'

const allowedFilters = ['days_from_now', 'type']

const days: string[] = []

for (let i = 0; i <= 6; i += 1){
  const day = DateTime.now().plus({ days: i })
  days.push(day.weekdayLong)
}

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'days_from_now',
      label: 'Showing Deadlines For: (Default Today)',
      values: days.map((day, i) => (({
        key: i,
        label: i === 0 ? `${day} (Today)` : day,
      }))),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'type',
      label: 'Deadline Type',
      values: [
        { key: 'booking', label: 'Booking' },
        { key: 'editorial', label: 'Editorial' },
        { key: 'inspection', label: 'Inspection' },
        { key: 'artwork', label: 'Artwork' },
        { key: 'ofi', label: 'OFI' },
      ],
    },
  ]
}

const PageContent = (props: { pageFilters: FilterOptions }) => {
  const { pageFilters } = props
  const { days_from_now } = pageFilters

  useLocation()

  const packContext = useContext(PackContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = packContext

  const sanitisedFilters = sanitiseObject(pageFilters, allowedFilters)

  const publicationEditionDeadlinesPayload = usePublicationEditionsDeadlines({
    // @ts-expect-error TS2741
    filters: sanitisedFilters,
    requestOptions: {
      include_publication_edition_deadlines: true,
      include_publication_edition_order_item_count: true,
      include_publication_edition_publication: true,
      include_unused_editions: true,
    },
    performHttpRequests: true,
  })

  const {
    loading, specialDeadlines, standardDeadlines,
  } = publicationEditionDeadlinesPayload

  return (
    <>
      <Helmet>
        <title>Deadlines | Realhub</title>
      </Helmet>

      <PageHeader
        nestedNavigation
        activeTabBarItemKey="deadlines"
        tabBarItems={[
          // @ts-expect-error TS2741
          {
            icon: faGlobe,
            key: 'deadlines',
            title: 'Deadlines',
          },
        ]}
        title="Deadlines"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && (
              <>
                <DeadlinesTable
                  publicationEditionDeadlines={standardDeadlines}
                  title="Deadlines"
                />

                <DeadlinesTable
                  publicationEditionDeadlines={specialDeadlines}
                  title="Special Deadlines"
                />
              </>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              externalFilters={{ days_from_now }}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
